import { useMemo } from 'react';
import { RankedBars } from '../../charts/RankedBars';
import { Card, ChartCard, DataTable, Stat, StatusChip, type Column } from '../../components/data';
import { ErrorBanner, Loading } from '../../components/ui';
import { formatCompactCurrency, num, pct, relativeDays, snakeLabel, urgency } from '../../lib/format';
import { useResource } from '../../lib/hooks';
import type { Employee } from '../../lib/types';
import { tierBand, type VendorRow, type VendorsData } from './adminTypes';
import { RegisterPanel, formatIsoShort, type RegisterSpec } from './adminUi';

const VENDOR_CATEGORY = [
  'distribution', 'hardware_oem', 'software', 'carrier', 'services', 'other',
] as const;
const VENDOR_TIER = ['strategic', 'preferred', 'transactional', 'watchlist'] as const;
const VENDOR_STATUS = ['active', 'under_review', 'exiting', 'inactive'] as const;

/**
 * The supplier base.
 *
 * NOT the same register as the CS module's vendor directory, and the difference
 * is worth knowing before wondering why a supplier appears twice. That one is the
 * CS team's launcher — portal URLs, named contacts, a logo — for the consoles an
 * engineer opens during a client audit. This one is the admin function's
 * commercial view: what we spend, at what margin, on what terms, how reliably
 * they ship, and when the relationship was last reviewed. Same company, two sets
 * of facts, two owners. See the header of migration 048.
 *
 * Spend concentration is the figure this tab exists for. A total spend number
 * says how much goes out; the top three suppliers' share of it says how exposed
 * the business is to one of them changing terms.
 */
export function AdminVendors({ canWrite, onChanged }: { canWrite: boolean; onChanged: () => void }) {
  const analytics = useResource<VendorsData>('/admin/analytics/vendors');
  const employees = useResource<Employee[]>('/employees?status=active');

  const spec = useMemo<RegisterSpec<VendorRow>>(
    () => ({
      resource: 'vendors',
      title: 'Suppliers',
      singular: 'Vendor',
      emptyTitle: 'No suppliers recorded yet',
      emptyBody:
        'Add the distributors, OEMs, software vendors and carriers this business buys from. Annual spend and the tier are what make the concentration and review figures on this tab mean anything — a small strategic supplier outranks a large transactional one when a QBR slot has to be given to somebody.',
      searchHint: 'Name, their account manager, or our owner',
      idOf: (row) => row.AdminVendorId,
      labelOf: (row) => row.Name,
      filters: [
        { param: 'tier', label: 'Tier', options: VENDOR_TIER },
        { param: 'category', label: 'Category', options: VENDOR_CATEGORY },
        { param: 'status', label: 'Status', options: VENDOR_STATUS },
      ],
      columns: [
        {
          key: 'Name',
          label: 'Vendor',
          render: (row) => (
            <div>
              <div className="admin-cell-title">{row.Name}</div>
              <div className="xs muted">
                {snakeLabel(row.Category)}
                {row.PaymentTerms ? ` · ${row.PaymentTerms}` : ''}
                {row.PurchaseOrderCount > 0
                  ? ` · ${row.PurchaseOrderCount} ${row.PurchaseOrderCount === 1 ? 'order' : 'orders'}`
                  : ''}
              </div>
            </div>
          ),
        },
        {
          key: 'Tier',
          label: 'Tier',
          render: (row) => (
            <div className="admin-due">
              <StatusChip band={tierBand(row.Tier)}>{snakeLabel(row.Tier)}</StatusChip>
              {row.Status !== 'active' && <span className="xs muted">{snakeLabel(row.Status)}</span>}
            </div>
          ),
        },
        {
          key: 'AnnualSpend',
          label: 'Annual spend',
          align: 'right',
          render: (row) => formatCompactCurrency(row.AnnualSpend),
        },
        {
          key: 'AvgMarginPercent',
          label: 'Margin',
          align: 'right',
          render: (row) =>
            row.AvgMarginPercent === null ? <span className="sub">—</span> : pct(row.AvgMarginPercent, 1),
        },
        {
          key: 'OnTimeShipPercent',
          label: 'On time',
          align: 'right',
          render: (row) =>
            row.OnTimeShipPercent === null ? (
              <span className="sub">—</span>
            ) : (
              pct(row.OnTimeShipPercent, 0)
            ),
        },
        {
          key: 'RelationshipScore',
          label: 'QBR',
          align: 'right',
          /* Out of ten, spelled out — a bare "7" invites being read as a
             percentage or a count. */
          render: (row) =>
            row.RelationshipScore === null ? (
              <span className="sub">unscored</span>
            ) : (
              `${row.RelationshipScore}/10`
            ),
        },
        {
          key: 'NextReviewDate',
          label: 'Next review',
          render: (row) =>
            row.NextReviewDate ? (
              <div className="admin-due">
                <span>{formatIsoShort(row.NextReviewDate)}</span>
                <StatusChip band={urgency(row.DaysToReview)}>{relativeDays(row.DaysToReview)}</StatusChip>
              </div>
            ) : (
              <span className="sub">not scheduled</span>
            ),
        },
        {
          key: 'InternalOwnerName',
          label: 'Our owner',
          render: (row) => row.InternalOwnerName ?? <span className="sub">—</span>,
        },
      ],
      fields: [
        { name: 'name', label: 'Vendor name', kind: 'text', wide: true },
        { name: 'category', label: 'Category', kind: 'select', options: VENDOR_CATEGORY },
        {
          name: 'tier',
          label: 'Tier',
          kind: 'select',
          options: VENDOR_TIER,
          hint: 'How much the relationship is worth managing — not the same as how much we spend.',
        },
        { name: 'status', label: 'Status', kind: 'select', options: VENDOR_STATUS },
        { name: 'paymentTerms', label: 'Payment terms', kind: 'text', placeholder: 'Net 30' },
        {
          name: 'accountManagerName',
          label: 'Their account manager',
          kind: 'text',
          hint: 'Our contact AT the vendor — free text, because they are not in this directory.',
        },
        { name: 'contactEmail', label: 'Contact email', kind: 'text' },
        { name: 'contactPhone', label: 'Contact phone', kind: 'text' },
        { name: 'portalUrl', label: 'Partner portal', kind: 'text', wide: true },
        { name: 'annualSpend', label: 'Annual spend', kind: 'money' },
        { name: 'avgMarginPercent', label: 'Average margin %', kind: 'percent' },
        { name: 'onTimeShipPercent', label: 'On-time ship %', kind: 'percent' },
        {
          name: 'relationshipScore',
          label: 'QBR score (1–10)',
          kind: 'int',
          hint: 'Leave blank rather than guessing — an unscored vendor is excluded from the average.',
        },
        { name: 'rebateEligible', label: 'Rebate eligible', kind: 'checkbox' },
        { name: 'lastReviewDate', label: 'Last reviewed', kind: 'date' },
        { name: 'nextReviewDate', label: 'Next review', kind: 'date' },
        {
          name: 'internalOwnerEmployeeId',
          label: 'Our owner',
          kind: 'select',
          optional: true,
          placeholder: 'Unassigned',
          choices: (employees.data ?? []).map((e) => ({
            value: String(e.EmployeeId),
            label: e.FullName,
          })),
        },
        { name: 'notes', label: 'Notes', kind: 'textarea', wide: true },
      ],
      blank: {
        name: '',
        category: 'distribution',
        tier: 'transactional',
        status: 'active',
        paymentTerms: '',
        accountManagerName: '',
        contactEmail: '',
        contactPhone: '',
        portalUrl: '',
        annualSpend: '',
        avgMarginPercent: '',
        onTimeShipPercent: '',
        relationshipScore: '',
        rebateEligible: false,
        lastReviewDate: '',
        nextReviewDate: '',
        internalOwnerEmployeeId: '',
        notes: '',
      },
      toForm: (row) => ({
        name: row.Name,
        category: row.Category,
        tier: row.Tier,
        status: row.Status,
        paymentTerms: row.PaymentTerms ?? '',
        accountManagerName: row.AccountManagerName ?? '',
        contactEmail: row.ContactEmail ?? '',
        contactPhone: row.ContactPhone ?? '',
        portalUrl: row.PortalUrl ?? '',
        annualSpend: row.AnnualSpend,
        avgMarginPercent: row.AvgMarginPercent ?? '',
        onTimeShipPercent: row.OnTimeShipPercent ?? '',
        relationshipScore: row.RelationshipScore ?? '',
        rebateEligible: row.RebateEligible,
        lastReviewDate: row.LastReviewDate?.slice(0, 10) ?? '',
        nextReviewDate: row.NextReviewDate?.slice(0, 10) ?? '',
        internalOwnerEmployeeId: row.InternalOwnerEmployeeId ?? '',
        notes: row.Notes,
      }),
    }),
    [employees.data],
  );

  const reload = () => {
    analytics.reload();
    onChanged();
  };

  const d = analytics.data;
  const s = d?.summary;

  return (
    <div className="admin-stack">
      {analytics.error && <ErrorBanner message={analytics.error} />}
      {!d && analytics.loading && <Loading label="Loading suppliers" />}

      {d && s && s.total > 0 && (
        <>
          <div className="grid grid-4">
            <Stat
              label="Annual spend"
              value={formatCompactCurrency(s.annualSpend)}
              foot={`across ${num(s.total)} ${s.total === 1 ? 'supplier' : 'suppliers'}`}
            />
            <Stat
              label="Top-3 concentration"
              value={s.spendConcentrationPercent === null ? '—' : pct(s.spendConcentrationPercent, 0)}
              /* The reason this figure is here, said on the tile. */
              foot="of spend with three suppliers"
            />
            <Stat
              label="QBRs overdue"
              value={s.reviewsOverdue}
              flagged={s.reviewsOverdue > 0}
              foot={
                s.noReviewScheduled > 0
                  ? `${s.noReviewScheduled} have no review scheduled`
                  : `${s.reviewsDue60} due within 60 days`
              }
            />
            <Stat
              label="Average QBR score"
              value={s.avgRelationshipScore === null ? '—' : `${num(s.avgRelationshipScore, 1)}/10`}
              foot={
                s.unscored > 0
                  ? `${s.unscored} unscored and excluded`
                  : 'across every scored supplier'
              }
            />
          </div>

          <div className="admin-split">
            <ChartCard
              title="Spend by supplier"
              subtitle="The ten largest, at annual spend."
              footer={
                s.avgOnTimeShipPercent === null
                  ? undefined
                  : `Average on-time shipping across suppliers that record it: ${pct(s.avgOnTimeShipPercent, 0)}.`
              }
              table={{
                columns: TOP_SPEND_COLUMNS,
                rows: d.topSpend,
                rowKey: (row) => String(row.adminVendorId),
              }}
            >
              <RankedBars
                rows={d.topSpend.map((v) => ({
                  label: v.name,
                  value: v.annualSpend,
                  meta: snakeLabel(v.tier),
                  /* Watchlist suppliers take the status colour: the point of the
                     tier is that spend with them is a risk, not just a number. */
                  color: v.tier === 'watchlist' ? 'var(--status-serious)' : undefined,
                }))}
                format={(v) => formatCompactCurrency(v)}
              />
            </ChartCard>

            <ChartCard
              title="Spend by tier"
              subtitle="Whether the money follows the relationships being managed."
              table={{
                columns: [
                  {
                    key: 'tier',
                    label: 'Tier',
                    render: (row: VendorsData['byTier'][number]) => snakeLabel(row.tier),
                  },
                  { key: 'count', label: 'Suppliers', align: 'right' },
                  {
                    key: 'annualSpend',
                    label: 'Annual spend',
                    align: 'right',
                    render: (row: VendorsData['byTier'][number]) =>
                      formatCompactCurrency(row.annualSpend),
                  },
                ],
                rows: d.byTier,
                rowKey: (row) => row.tier,
              }}
            >
              <RankedBars
                rows={d.byTier.map((t) => ({
                  label: snakeLabel(t.tier),
                  value: t.annualSpend,
                  meta: `${t.count} ${t.count === 1 ? 'supplier' : 'suppliers'}`,
                }))}
                format={(v) => formatCompactCurrency(v)}
                labelWidth={132}
              />
            </ChartCard>
          </div>

          <ChartCard
            title="Spend by category"
            subtitle="What kind of supplier the money goes to."
            table={{
              columns: [
                {
                  key: 'category',
                  label: 'Category',
                  render: (row: VendorsData['byCategory'][number]) => snakeLabel(row.category),
                },
                { key: 'count', label: 'Suppliers', align: 'right' },
                {
                  key: 'annualSpend',
                  label: 'Annual spend',
                  align: 'right',
                  render: (row: VendorsData['byCategory'][number]) =>
                    formatCompactCurrency(row.annualSpend),
                },
              ],
              rows: d.byCategory,
              rowKey: (row) => row.category,
            }}
          >
            <RankedBars
              rows={d.byCategory.map((c) => ({
                label: snakeLabel(c.category),
                value: c.annualSpend,
                meta: `${c.count} ${c.count === 1 ? 'supplier' : 'suppliers'}`,
              }))}
              format={(v) => formatCompactCurrency(v)}
              labelWidth={132}
            />
          </ChartCard>

          <Card
            title="Reviews due"
            subtitle="Suppliers whose QBR is overdue or falls within 90 days, soonest first."
          >
            <DataTable
              columns={REVIEW_COLUMNS}
              rows={d.reviewsDue}
              rowKey={(row) => String(row.adminVendorId)}
              empty={
                s.noReviewScheduled === s.total
                  ? 'No supplier has a review date set, so nothing can be due.'
                  : 'No reviews are due in the next 90 days.'
              }
            />
          </Card>
        </>
      )}

      <RegisterPanel spec={spec} canWrite={canWrite} onChanged={reload} />
    </div>
  );
}

const TOP_SPEND_COLUMNS: Column<VendorsData['topSpend'][number]>[] = [
  { key: 'name', label: 'Vendor' },
  { key: 'tier', label: 'Tier', render: (row) => <StatusChip band={tierBand(row.tier)}>{snakeLabel(row.tier)}</StatusChip> },
  {
    key: 'annualSpend',
    label: 'Annual spend',
    align: 'right',
    render: (row) => formatCompactCurrency(row.annualSpend),
  },
  {
    key: 'avgMarginPercent',
    label: 'Margin',
    align: 'right',
    render: (row) => (row.avgMarginPercent === null ? '—' : pct(row.avgMarginPercent, 1)),
  },
  {
    key: 'onTimeShipPercent',
    label: 'On time',
    align: 'right',
    render: (row) => (row.onTimeShipPercent === null ? '—' : pct(row.onTimeShipPercent, 0)),
  },
  {
    key: 'relationshipScore',
    label: 'QBR',
    align: 'right',
    render: (row) => (row.relationshipScore === null ? 'unscored' : `${row.relationshipScore}/10`),
  },
];

const REVIEW_COLUMNS: Column<VendorsData['reviewsDue'][number]>[] = [
  { key: 'name', label: 'Vendor' },
  { key: 'tier', label: 'Tier', render: (row) => <StatusChip band={tierBand(row.tier)}>{snakeLabel(row.tier)}</StatusChip> },
  {
    key: 'annualSpend',
    label: 'Annual spend',
    align: 'right',
    render: (row) => formatCompactCurrency(row.annualSpend),
  },
  {
    key: 'lastReviewDate',
    label: 'Last reviewed',
    render: (row) => (row.lastReviewDate ? formatIsoShort(row.lastReviewDate) : <span className="sub">never</span>),
  },
  {
    key: 'nextReviewDate',
    label: 'Due',
    render: (row) => (
      <div className="admin-due">
        <span>{formatIsoShort(row.nextReviewDate)}</span>
        <StatusChip band={urgency(row.daysToReview)}>{relativeDays(row.daysToReview)}</StatusChip>
      </div>
    ),
  },
  {
    key: 'relationshipScore',
    label: 'QBR',
    align: 'right',
    render: (row) => (row.relationshipScore === null ? 'unscored' : `${row.relationshipScore}/10`),
  },
  {
    key: 'internalOwnerName',
    label: 'Our owner',
    render: (row) => row.internalOwnerName ?? <span className="sub">—</span>,
  },
];
